import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { tr, PLANS_BY_LANG } from "@/lib/i18n";
import { Plus, Trash2, Send, MessagesSquare, FileText, RefreshCw, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import PrivacyBadge from "@/components/PrivacyBadge";
import { useCheckoutConsents, CheckoutConsentCheckboxes, checkoutPayload } from "@/components/CheckoutConsent";

const UNLOCK_PLAN_IDS = ["leo_start", "leo_monthly", "leo_light", "leo_annual"];

// Very small markdown renderer: **bold** and *italic*. Preserves newlines
// via <span style="white-space: pre-wrap"> on the parent. Not full markdown;
// just enough so Leo's guidance replies (e.g. "click **Career Path**") don't
// show literal asterisks in the chat bubble.
function renderInlineMarkdown(text) {
  if (!text) return null;
  const parts = [];
  const re = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**") && tok.endsWith("**")) {
      parts.push(<strong key={`b${key++}`} className="text-stone-100">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("*") && tok.endsWith("*")) {
      parts.push(<em key={`i${key++}`}>{tok.slice(1, -1)}</em>);
    } else {
      parts.push(tok);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export default function LeoChat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang, user, access, refreshUser } = useAuth();
  const t = (k) => tr(k, lang);

  const [chats, setChats] = useState([]);
  const [chat, setChat] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [scenePickerOpen, setScenePickerOpen] = useState(false);
  const [savedScenes, setSavedScenes] = useState(null); // null = not yet loaded, [] = empty
  const [attachingSceneId, setAttachingSceneId] = useState(null);
  const bottomRef = useRef(null);
  const activationBootRef = useRef(false); // guard against double-boot in StrictMode
  const onboardingBootRef = useRef(false);

  const isActivationCareer = searchParams.get("src") === "activation-career";
  const isFirstOnboarding = searchParams.get("src") === "first-onboarding";
  const [onboardingDone, setOnboardingDone] = useState(false);

  const loadChats = async () => {
    try { const r = await api.get("/leo/chats"); setChats(r.data); } catch {}
  };

  const openScenePicker = async () => {
    setScenePickerOpen(true);
    if (savedScenes !== null) return;
    try {
      const r = await api.get("/scenes");
      setSavedScenes(r.data || []);
    } catch {
      setSavedScenes([]);
    }
  };

  const pickSavedScene = async (sceneId) => {
    if (!chat) return;
    setAttachingSceneId(sceneId);
    try {
      const s = await api.get(`/scenes/${sceneId}`);
      const text = s.data?.scene_text || "";
      await api.patch(`/leo/chats/${chat.chat_id}/scene`, { scene_text: text });
      setChat(c => ({ ...c, scene_text: text }));
      setScenePickerOpen(false);
      toast.success(lang === "pl" ? "Scena dołączona. Leo już ją widzi." : "Scene attached. Leo can see it.");
    } catch {
      toast.error(t("mir.fail"));
    } finally {
      setAttachingSceneId(null);
    }
  };

  const loadChat = async (chatId) => {
    if (!chatId) { setChat(null); return; }
    try {
      const r = await api.get(`/leo/chats/${chatId}`);
      setChat(r.data);
      // If empty conversation, ask Leo to open
      if (!r.data.messages || r.data.messages.length === 0) {
        const src = r.data.activation_source;
        const url =
          src === "career" ? `/leo/chats/${chatId}/activation-opener`
          : src === "first_onboarding" ? `/leo/chats/${chatId}/onboarding-opener`
          : `/leo/chats/${chatId}/opening`;
        const o = await api.post(url);
        setChat(c => ({ ...(c || r.data), messages: o.data.history }));
      }
    } catch {}
  };

  useEffect(() => { loadChats(); }, []);
  useEffect(() => { loadChat(id); }, [id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat?.messages?.length]);

  // Post-tutorial "Work on my career" entry point: /leo?src=activation-career
  // with NO chat id. Auto-create a chat, seed the pre-baked opener via the
  // activation-opener endpoint (no LLM call), then land the user inside it.
  useEffect(() => {
    if (!isActivationCareer || id) return;
    if (activationBootRef.current) return;
    activationBootRef.current = true;
    (async () => {
      try {
        const created = await api.post("/leo/chats", { title: null, scene_text: "" });
        const newId = created.data.chat_id;
        await api.post(`/leo/chats/${newId}/activation-opener`);
        trackEvent("career_chat_started_from_activation");
        await loadChats();
        // Preserve the src param so downstream analytics still see the source
        // but the URL now points to a real chat id.
        navigate(`/leo/${newId}?src=activation-career`, { replace: true });
      } catch (e) {
        // Fall back to a normal chat if the activation opener fails.
        activationBootRef.current = false;
        toast.error(t("an.fail"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivationCareer, id]);

  // First-onboarding entry (spec §1+§3): /leo?src=first-onboarding with no id.
  // Mirrors the activation-career bootstrap pattern. Zero LLM here — the
  // opener endpoint writes a static message and the WOW is triggered by
  // the user's next real message.
  useEffect(() => {
    if (!isFirstOnboarding || id) return;
    if (onboardingBootRef.current) return;
    onboardingBootRef.current = true;
    (async () => {
      try {
        const created = await api.post("/leo/chats", { title: null, scene_text: "" });
        const newId = created.data.chat_id;
        await api.post(`/leo/chats/${newId}/onboarding-opener`);
        await loadChats();
        navigate(`/leo/${newId}?src=first-onboarding`, { replace: true });
      } catch (e) {
        onboardingBootRef.current = false;
        toast.error(t("an.fail"));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstOnboarding, id]);

  const newChat = async () => {
    try {
      const r = await api.post("/leo/chats", { title: null, scene_text: "" });
      await loadChats();
      navigate(`/leo/${r.data.chat_id}`);
    } catch { toast.error(t("an.fail")); }
  };

  const removeChat = async (chatId) => {
    const label = lang === "pl" ? "Usunąć tę rozmowę?" : "Delete this conversation?";
    if (!window.confirm(label)) return;
    try {
      await api.delete(`/leo/chats/${chatId}`);
      await loadChats();
      if (chat?.chat_id === chatId) navigate("/leo");
    } catch { toast.error(t("an.fail")); }
  };

  const [chatLimitReached, setChatLimitReached] = useState(false);

  const send = async () => {
    if (!input.trim() || sending || !chat || chatLimitReached) return;
    const text = input;
    setInput("");
    setSending(true);
    // optimistic user msg
    setChat(c => ({ ...c, messages: [...(c.messages || []), { role: "user", content: text, ts: new Date().toISOString() }] }));
    try {
      const r = await api.post(`/leo/chats/${chat.chat_id}/message`, { message: text });
      setChat(c => ({ ...c, messages: r.data.history, title: r.data.title || c.title }));
      loadChats();
      // Activation-career: emit analytics once Leo points the user to
      // Career Path → + Add New Goal. Backend flags this via guidance_given.
      if (r.data.guidance_given) {
        trackEvent("career_goal_guidance_given", { chat_id: chat.chat_id });
      }
      // First-onboarding completion (spec §7). Backend already saved the
      // profile + completion flag in the same AI call. UI: show the CTA
      // button, do NOT auto-redirect. Refresh AuthContext.user from
      // /auth/me so `needs_first_onboarding` flips to false immediately —
      // otherwise ProtectedRoute would keep redirecting CRM/Career/etc.
      // back to /leo?src=first-onboarding and bootstrap another chat.
      if (r.data.onboarding_complete) {
        setOnboardingDone(true);
        try { await refreshUser?.(); } catch { /* ignore */ }
      }
      // Chat cap (spec §8). Block further input; show the message.
      if (r.data.chat_limit_reached) setChatLimitReached(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("reh.send_fail"));
    } finally { setSending(false); }
  };

  const [busy, setBusy] = useState(null);
  const { consentOf, setConsent, canBuy } = useCheckoutConsents();
  const buy = async (planId) => {
    if (!user) { navigate("/login"); return; }
    if (!canBuy(planId)) return;
    const suffix = lang === "pl" ? "_pln" : "_usd";
    const effective_key = `${planId}${suffix}`;
    setBusy(planId);
    try {
      const r = await api.post("/payments/checkout", checkoutPayload(effective_key));
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "en" ? "Could not create payment." : "Nie udało się utworzyć płatności."));
      setBusy(null);
    }
  };

  if (access && access.has_access === false) {
    const plans = PLANS_BY_LANG[lang] || PLANS_BY_LANG.pl;
    const unlockPlans = UNLOCK_PLAN_IDS
      .map((id) => plans.find((p) => p.id === id))
      .filter(Boolean);
    const heading = lang === "en"
      ? "Unlock LEO Career Chat"
      : "Odblokuj rozmowę z LEO Career Chat";
    const sub = lang === "en"
      ? "Choose a plan to keep working on your career with LEO."
      : "Wybierz plan, aby dalej pracować nad karierą z LEO.";
    const choosePrefix = lang === "en" ? "Choose" : "Wybieram";
    const opening = lang === "en" ? "Opening Stripe…" : "Otwieram Stripe…";
    return (
      <div className="max-w-4xl" data-testid="leo-chat-paywall">
        <section className="mt-4 paper p-6 sm:p-8" data-testid="leo-chat-paywall-section">
          <h3 className="serif text-2xl sm:text-3xl text-stone-100">{heading}</h3>
          <p className="text-stone-400 mt-3 text-sm leading-relaxed">{sub}</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {unlockPlans.map((plan) => (
              <div
                key={plan.id}
                data-testid={`leo-chat-paywall-plan-${plan.id}`}
                className="border border-stone-800 rounded-2xl p-5 bg-stone-900/60 flex flex-col"
              >
                <div className="serif text-xl text-stone-100">{plan.name}</div>
                <div className="mono text-xs uppercase tracking-widest text-amber-400 mt-2">
                  {plan.price} <span className="text-stone-500">{plan.priceSuffix || ""}</span>
                </div>
                <CheckoutConsentCheckboxes
                  lang={lang}
                  planId={plan.id}
                  consent={consentOf(plan.id)}
                  onChange={(k, v) => setConsent(plan.id, k, v)}
                  testidPrefix="leo-chat-paywall-consent"
                />
                <div className="flex-1" />
                <button
                  data-testid={`leo-chat-paywall-buy-${plan.id}`}
                  disabled={busy === plan.id || !canBuy(plan.id)}
                  onClick={() => buy(plan.id)}
                  className="pill-btn mt-4 w-full disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy === plan.id ? opening : `${choosePrefix} ${plan.name.replace(/^LEO\s+/i, "")}`}
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-6 -mt-2">
      {/* Sidebar */}
      <aside className="border border-stone-900 rounded-sm">
        <div className="p-3 flex items-center justify-between border-b border-stone-900">
          <div className="mono text-[11px] tracking-[0.3em] text-amber-500 uppercase">Leo · {lang === "pl" ? "Rozmowy" : "Chats"}</div>
          <button data-testid="leo-new-chat-btn" onClick={newChat} className="ghost-btn !py-1.5 !px-2 !text-xs">
            <Plus size={12}/> {lang === "pl" ? "Nowa" : "New"}
          </button>
        </div>
        <ul className="max-h-[65vh] overflow-y-auto">
          {chats.length === 0 ? (
            <li className="p-4 text-stone-500 text-xs mono">{lang === "pl" ? "brak rozmów" : "no conversations"}</li>
          ) : chats.map(c => (
            <li key={c.chat_id}>
              <div className={`flex items-center gap-2 px-3 py-2.5 border-b border-stone-900 hover:bg-stone-900/60 ${chat?.chat_id === c.chat_id ? "bg-stone-900/60" : ""}`}>
                <button
                  data-testid={`leo-chat-open-${c.chat_id}`}
                  onClick={() => navigate(`/leo/${c.chat_id}`)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="serif text-stone-100 truncate text-sm">{c.title || "—"}</div>
                  <div className="mono text-[10px] text-stone-500 uppercase tracking-widest mt-0.5">
                    {new Date(c.updated_at).toLocaleString(lang === "pl" ? "pl-PL" : "en-US", { dateStyle: "short", timeStyle: "short" })}
                  </div>
                </button>
                <button onClick={() => removeChat(c.chat_id)} className="text-stone-500 hover:text-rose-400 p-1">
                  <Trash2 size={12}/>
                </button>
              </div>
            </li>
          ))}
        </ul>
      </aside>

      {/* Main chat area */}
      <section>
        {!chat ? (
          <div className="paper p-10 text-center">
            <MessagesSquare className="mx-auto text-amber-500" size={32}/>
            <h1 className="serif text-3xl text-stone-100 mt-4">
              {lang === "pl" ? "Pracuj z Leo" : "Work with Leo"}
            </h1>
            <p className="text-stone-400 mt-2 max-w-lg mx-auto">
              {lang === "pl"
                ? "Scena, casting, agent, nowy rynek, networking albo kolejny krok w karierze. Powiedz Leo, nad czym chcesz pracować — poprowadzi Cię dalej."
                : "A scene, an audition, finding an agent, breaking into a new market, networking, or figuring out your next career move. Tell Leo what you want to work on, and he'll guide you from there."}
            </p>
            <button data-testid="leo-start-first-btn" onClick={newChat} className="pill-btn mt-8"><Plus size={14}/> {lang === "pl" ? "Rozpocznij rozmowę" : "Start a conversation"}</button>
          </div>
        ) : (
          <div className="paper p-0 flex flex-col h-[75vh]">
            <div className="border-b border-stone-800 p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <button onClick={()=>navigate("/leo")} className="mono text-[10px] text-stone-500 hover:text-amber-500 uppercase tracking-widest flex items-center gap-1"><ChevronLeft size={11}/>{lang === "pl" ? "Wszystkie" : "All"}</button>
                <div className="serif text-xl text-stone-100 truncate mt-1" data-testid="leo-chat-title">{chat.title || "—"}</div>
                {chat.scene_text && (
                  <div className="mono text-[10px] text-amber-500/80 uppercase tracking-widest mt-1">
                    <FileText size={10} className="inline mr-1"/>{lang === "pl" ? "scena dołączona" : "scene attached"} · {chat.scene_text.length} {lang === "pl" ? "znaków" : "chars"}
                  </div>
                )}
              </div>
              <div className="shrink-0">
                <button
                  data-testid="leo-pick-saved-scene-btn"
                  onClick={openScenePicker}
                  className="ghost-btn !py-2 !text-xs"
                >
                  <FileText size={12}/> {lang === "pl" ? "Wybierz zapisaną scenę" : "Choose a saved scene"}
                </button>
              </div>
            </div>

            {scenePickerOpen && (
              <div className="border-b border-stone-800 p-4" data-testid="leo-saved-scenes-picker">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="mono text-[10px] text-stone-500 uppercase tracking-widest">
                    {lang === "pl" ? "Twoje zapisane sceny" : "Your saved scenes"}
                  </div>
                  <button
                    data-testid="leo-saved-scenes-close"
                    onClick={() => setScenePickerOpen(false)}
                    className="mono text-[10px] text-stone-500 hover:text-amber-500 uppercase tracking-widest"
                  >
                    {t("action.close")}
                  </button>
                </div>
                {savedScenes === null ? (
                  <div className="mono text-stone-500 text-xs">{t("dash.loading")}</div>
                ) : savedScenes.length === 0 ? (
                  <div data-testid="leo-saved-scenes-empty" className="text-stone-400 text-sm">
                    {lang === "pl"
                      ? "Nie masz jeszcze zapisanych scen. Przygotuj scenę w Scene Prep."
                      : "You don't have any saved scenes yet. Prepare a scene in Scene Prep."}
                  </div>
                ) : (
                  <ul className="space-y-1.5 max-h-56 overflow-y-auto">
                    {savedScenes.map((s) => (
                      <li key={s.scene_id}>
                        <button
                          data-testid={`leo-saved-scene-${s.scene_id}`}
                          disabled={attachingSceneId === s.scene_id}
                          onClick={() => pickSavedScene(s.scene_id)}
                          className="w-full text-left paper p-3 hover:border-amber-500/50 transition-colors disabled:opacity-60"
                        >
                          <div className="serif text-stone-100 text-sm truncate">{s.title || "—"}</div>
                          <div className="mono text-[10px] text-stone-500 uppercase tracking-widest mt-0.5">
                            {new Date(s.created_at).toLocaleDateString(lang === "pl" ? "pl-PL" : "en-US", { dateStyle: "short" })}
                            {attachingSceneId === s.scene_id && (
                              <span className="ml-2 text-amber-500">
                                {lang === "pl" ? "dołączam…" : "attaching…"}
                              </span>
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div translate="no" data-testid="leo-transcript" className="notranslate flex-1 overflow-y-auto p-5 space-y-4">
              <AnimatePresence initial={false}>
                {(chat.messages || []).map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid={`leo-msg-${m.role}-${i}`}
                    className={m.role === "leo" ? "" : "text-right"}
                  >
                    <div className={`mono text-[10px] tracking-widest uppercase ${m.role === "leo" ? "text-amber-500/70" : "text-stone-500"}`}>
                      {m.role === "leo" ? "Leo" : (lang === "pl" ? "Ty" : "You")}
                    </div>
                    <div className={`mt-1 whitespace-pre-wrap leading-relaxed ${
                      m.role === "leo"
                        ? "text-stone-200 serif text-lg"
                        : "text-stone-300 mono text-[14px] inline-block bg-stone-900/60 px-3 py-2 rounded-sm"
                    }`}>{m.role === "leo" ? renderInlineMarkdown(m.content) : m.content}</div>
                  </motion.div>
                ))}
              </AnimatePresence>
              {sending && (
                <div className="text-amber-500/80 mono text-xs tracking-widest">
                  <RefreshCw size={12} className="inline animate-spin mr-2"/>{lang === "pl" ? "Leo myśli…" : "Leo is thinking…"}
                </div>
              )}
              <div ref={bottomRef}/>
            </div>

            {isFirstOnboarding && onboardingDone && (
              <div className="px-4 pt-3">
                <button
                  data-testid="onboarding-show-me-leo"
                  onClick={() => navigate("/dashboard")}
                  className="pill-btn w-full"
                >
                  {lang === "pl" ? "POKAŻ MI LEO" : "SHOW ME LEO"}
                </button>
              </div>
            )}
            {chatLimitReached && (
              <div className="px-4 pt-3 mono text-xs text-amber-500/90">
                {lang === "pl"
                  ? "Limit rozmów z LEO w tym planie został wykorzystany."
                  : "Your LEO chat allowance for this plan has been used."}
              </div>
            )}

            <div className="border-t border-stone-800 p-4 flex gap-2 items-center">
              <input
                data-testid="leo-chat-input"
                value={input}
                onChange={(e)=>setInput(e.target.value)}
                onKeyDown={(e)=>{ if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }}}
                className="script-input flex-1 text-sm"
                placeholder={lang === "pl" ? "Napisz do Leo…" : "Write to Leo…"}
                disabled={chatLimitReached}
              />
              <button data-testid="leo-chat-send-btn" onClick={send} disabled={sending || !input.trim() || chatLimitReached} className="pill-btn !px-4">
                <Send size={14}/>
              </button>
            </div>
            <div className="px-4 pb-3">
              <PrivacyBadge />
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
